/**
 * VGreen cart grouping — cascade-remove bundle members.
 *
 * Why this exists: when a customer adds a configured strap with companion
 * upcharges (engraving, leather surcharge, extra-long, etc.), the strap and
 * each companion land in the cart as separate line items. Before this file,
 * a customer could remove just the engraving companion in cart and keep
 * the strap at its base price — yet the strap LIPs still said
 * "Engraving: Yes / Engraving text: ABC", so fulfillment would engrave
 * the strap and the order would lose the engraving surcharge. Same hole
 * for extra-long surcharges, surcharged leathers, and any other companion.
 *
 * How it works:
 *   1. When the configurator adds the bundle to cart it tags every line
 *      with two hidden LIPs:
 *        _vgreen_group_id   — random UUID shared by all bundle members
 *        _vgreen_group_size — total expected line count (parent + companions)
 *      Property names starting with `_` are hidden from the customer-facing
 *      cart line item details by Shopify convention.
 *
 *   2. On every page (loaded site-wide from layout/theme.liquid), this
 *      script:
 *        a) Fetches /cart.js and builds a map of group_id → member lines.
 *        b) Capture-phase click listener on the document watches for clicks
 *           on cart-remove-button / [data-cart-remove] / /cart/change links.
 *           When the targeted line is part of a multi-member bundle, the
 *           click is preventDefault'd, a window.confirm shows "this will
 *           also remove [other titles]", and on confirm all group members
 *           are removed in a single /cart/update.js call.
 *        c) Subscribes to PUB_SUB_EVENTS.cartUpdate so any cart change
 *           that leaves a group with fewer items than its expected size
 *           triggers silent cleanup of the remaining members. This is the
 *           safety net for cases the click interceptor misses (direct
 *           /cart/change URLs, qty input dropped to 0, third-party widgets).
 *
 * Direction of cascade is symmetric: removing any line in a group removes
 * them all. The strap (parent) and each companion are interchangeable from
 * this file's perspective — group membership is the only thing that
 * matters.
 *
 * Compatibility: relies on Dawn's pubsub (assets/pubsub.js) and the
 * PUB_SUB_EVENTS constant. Both ship with Dawn by default. If a future
 * theme bump removes them, the click interceptor still works; only the
 * post-hoc reconciliation goes silent.
 */
(function () {
  'use strict';

  // groupId -> { items: [{key, title, variant}], expectedSize }
  let groupMembers = {};
  // line_key -> groupId
  let keyToGroup = {};
  // Dawn renders cart rows with id="CartItem-N" / "CartDrawer-Item-N" where
  // N is the 1-based line INDEX, not the line key. To translate the index
  // we read out of the DOM into a usable cart line key, keep a parallel map
  // built from /cart.js order (Shopify guarantees items array order matches
  // line index — item N is cart.items[N-1]).
  let indexToKey = {};
  // Suppress re-entrant reconciliation while we're mid-cascade. Without
  // this, our own /cart/update.js call publishes a cartUpdate event,
  // which fires our subscriber, which sees the group is now broken
  // (we just removed the last members), and tries to clean up again.
  let reconcileInProgress = false;

  async function refreshCartMap() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!r.ok) return null;
      const cart = await r.json();
      const nextGroups = {};
      const nextKeyMap = {};
      const nextIdxMap = {};
      cart.items.forEach((item, i) => {
        // 1-based index matches Dawn's id="CartItem-N" / "CartDrawer-Item-N"
        nextIdxMap[i + 1] = item.key;
      });
      cart.items.forEach((item) => {
        const props = item.properties || {};
        const gid = props._vgreen_group_id;
        if (!gid) return;
        nextKeyMap[item.key] = gid;
        if (!nextGroups[gid]) {
          nextGroups[gid] = {
            items: [],
            expectedSize: parseInt(props._vgreen_group_size || '0', 10),
          };
        }
        nextGroups[gid].items.push({
          key: item.key,
          // product_title is the bare product name without variant info,
          // which reads cleanest in the confirm dialog.
          title: item.product_title || item.title || 'this item',
          variant: item.variant_title || '',
          // Role drives the modal copy. 'parent' = the configured strap;
          // 'companion' = a paid add-on (engraving, surcharged leather,
          // extra-long, etc.). Older bundles created before this property
          // existed will have role=undefined; the modal falls back to
          // generic copy in that case.
          role: item.properties._vgreen_group_role || null,
        });
      });
      groupMembers = nextGroups;
      keyToGroup = nextKeyMap;
      indexToKey = nextIdxMap;
      return cart;
    } catch (e) {
      console.warn('[VGreen cart-grouping] refresh failed:', e);
      return null;
    }
  }

  function extractLineKey(el) {
    if (!el || !el.closest) return null;

    // Strategy 1: explicit data-line-key (most reliable when present)
    const dataLineEl = el.closest('[data-line-key]');
    if (dataLineEl) {
      const k = dataLineEl.getAttribute('data-line-key');
      if (k) return k;
    }

    // Strategy 2: parse href on a /cart/change link, prefer ?id=KEY when
    // present (it's the actual line key, not the index).
    const link = el.closest('a[href*="/cart/change"]');
    if (link) {
      try {
        const url = new URL(link.href, location.origin);
        const idParam = url.searchParams.get('id');
        if (idParam) return idParam;
        // Some Dawn variants use ?line=N (1-based index). Translate via
        // indexToKey from the most recent cart fetch.
        const lineParam = url.searchParams.get('line');
        if (lineParam) {
          const k = indexToKey[parseInt(lineParam, 10)];
          if (k) return k;
        }
      } catch (_) {}
    }

    // Strategy 3: Dawn line container id "CartItem-N" / "CartDrawer-Item-N".
    // These hold the 1-based line index — translate to key.
    const lineContainer = el.closest(
      '[id^="CartItem-"], [id^="CartDrawer-Item-"]'
    );
    if (lineContainer) {
      const m = lineContainer.id.match(/^Cart(?:Drawer-)?Item-(.+)$/);
      if (m) {
        const captured = m[1];
        // If purely numeric, it's an index — translate.
        if (/^\d+$/.test(captured)) {
          const k = indexToKey[parseInt(captured, 10)];
          if (k) return k;
        } else {
          // Some themes inline the actual key — return as-is.
          return captured;
        }
      }
    }

    return null;
  }

  function findRemoveTrigger(target) {
    if (!target || !target.closest) return null;
    // Dawn's cart-remove-button, generic data-cart-remove, or any /cart/change link
    return target.closest(
      'cart-remove-button, cart-remove-button a, [data-cart-remove], a[href*="/cart/change"]'
    );
  }

  async function cascadeRemove(groupId) {
    const group = groupMembers[groupId];
    if (!group || group.items.length === 0) return;

    const updates = {};
    group.items.forEach((it) => {
      updates[it.key] = 0;
    });

    // Mirror Dawn's product-form pattern: request section re-renders so the
    // cart-items / cart-drawer UI updates inline rather than needing a full
    // page reload.
    const cartItemsEl =
      document.querySelector('cart-items') ||
      document.querySelector('cart-drawer-items');
    let sections = null;
    if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
      try {
        sections = cartItemsEl
          .getSectionsToRender()
          .map((s) => s.section || s.id)
          .filter(Boolean)
          .join(',');
      } catch (_) {}
    }

    const body = { updates };
    if (sections) {
      body.sections = sections;
      body.sections_url = location.pathname;
    }

    reconcileInProgress = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error('cart/update.js failed (' + res.status + '): ' + txt.slice(0, 200));
      }
      const cartData = await res.json();

      // Render the section fragments Dawn returned in the response. Without
      // this, the cart items get removed but the subtotal/total in
      // main-cart-footer stays showing the old amount — only some Dawn
      // components subscribe to cartUpdate; main-cart-footer doesn't.
      // Replicates the section-render logic in Dawn's cart-items.js so the
      // /cart page and the cart drawer both update inline.
      renderCartSections(cartData);

      // Toggle is-empty classes so Dawn's CSS shows the empty-cart layout
      // when the cascade left the cart with zero items.
      const itemCount = (cartData && typeof cartData.item_count === 'number') ? cartData.item_count : null;
      if (itemCount !== null) {
        ['cart-items', 'cart-drawer'].forEach((tag) => {
          document.querySelectorAll(tag).forEach((el) =>
            el.classList.toggle('is-empty', itemCount === 0)
          );
        });
        const footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', itemCount === 0);
      }

      // Then publish so any other subscribers (cart-notification, header
      // bubble, etc.) refresh too.
      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'vgreen-cart-grouping',
          cartData,
        });
      }

      await refreshCartMap();
    } catch (e) {
      console.error('[VGreen cart-grouping] cascade remove failed:', e);
    } finally {
      reconcileInProgress = false;
    }
  }

  // ------------------------------------------------------------------
  // 1. Pre-removal click interceptor — shows the confirm modal
  // ------------------------------------------------------------------
  // Capture phase so we run BEFORE Dawn's cart-remove-button handler.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = findRemoveTrigger(event.target);
      if (!trigger) return;

      const lineKey = extractLineKey(trigger);
      if (!lineKey) return;

      const groupId = keyToGroup[lineKey];
      if (!groupId) return; // not a bundled line — let Dawn handle normally

      const group = groupMembers[groupId];
      if (!group || group.items.length < 2) return; // single-line group, no cascade needed

      event.preventDefault();
      event.stopImmediatePropagation();

      // Build a friendly list of the OTHER items in the bundle.
      const clicked = group.items.find((it) => it.key === lineKey);
      const others = group.items
        .filter((it) => it.key !== lineKey)
        .map((it) => '• ' + it.title)
        .join('\n');

      // Branch the modal copy on the clicked item's role.
      //   parent     — the custom strap itself. Concise: "removing this
      //                will also remove the paid add-ons listed below".
      //   companion  — a paid add-on (engraving, extra-long, surcharged
      //                leather, etc.). Longer: explain WHY it's in the
      //                cart, then list what else goes away.
      //   unknown    — generic fallback for legacy bundles created before
      //                _vgreen_group_role existed.
      let message;
      const role = clicked && clicked.role;
      if (role === 'parent') {
        message =
  'このストラップを削除すると、セットになっている有料カスタマイズ' +
  'オプションもすべて削除されます：\n' +
  others +
  '\n\n続行しますか？';
      } else if (role === 'companion') {
        message =
  'この商品は、カスタムストラップで有料のカスタマイズオプションを' +
  '選択されたためカートに追加されています。ストラップと各有料' +
  'オプションはセットになっています。\n\n' +
  '削除すると、セットの他の商品もすべて削除されます：\n' +
  others +
  '\n\n続行しますか？';
      } else {
        message =
  'この商品はカスタムストラップとセットになっています。\n\n' +
  '削除すると、以下の商品も削除されます：\n' +
  others +
  '\n\n続行しますか？';
      }

      if (!window.confirm(message)) return; // customer cancelled — leave cart as-is

      cascadeRemove(groupId);
    },
    true
  );

  // ------------------------------------------------------------------
  // 2. Post-removal reconciliation — silent cleanup if a group was
  // partially removed via a path the click interceptor didn't catch.
  // ------------------------------------------------------------------
  async function reconcileBrokenGroups() {
    if (reconcileInProgress) return;
    const cart = await refreshCartMap();
    if (!cart) return;

    // A group is "broken" if its current member count is less than the
    // size it was created with. We delete every remaining member of any
    // broken group.
    const brokenGroupIds = Object.keys(groupMembers).filter((gid) => {
      const g = groupMembers[gid];
      return g.items.length > 0 && g.items.length < g.expectedSize;
    });

    for (let i = 0; i < brokenGroupIds.length; i++) {
      await cascadeRemove(brokenGroupIds[i]);
    }
  }

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.cartUpdate
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Ignore events we published ourselves (avoids infinite loop).
      if (event && event.source === 'vgreen-cart-grouping') return;
      // Slight delay so Dawn's own cart re-render lands first.
      setTimeout(reconcileBrokenGroups, 50);
    });
  }

  // Render Dawn cart section fragments returned by /cart/*.js when called
  // with the `sections=` param. Mirrors the logic in Dawn's cart-items.js
  // updateQuantity success handler so the /cart page subtotal + cart drawer
  // totals update inline after our cascade.
  function renderCartSections(cartData) {
    if (!cartData || !cartData.sections) return;
    const itemsEl =
      document.querySelector('cart-items') ||
      document.querySelector('cart-drawer-items');
    if (!itemsEl || typeof itemsEl.getSectionsToRender !== 'function') return;

    let sectionsList;
    try {
      sectionsList = itemsEl.getSectionsToRender();
    } catch (_) {
      return;
    }
    if (!Array.isArray(sectionsList)) return;

    sectionsList.forEach((section) => {
      const targetId = section.id;
      if (!targetId) return;
      const target = document.getElementById(targetId);
      if (!target) return;

      // Section payload key is section.section (the actual Shopify section
      // id). When section.section is undefined (older Dawn versions), fall
      // back to section.id.
      const payloadKey = section.section || targetId;
      const html = cartData.sections[payloadKey];
      if (!html) return;

      // Parse the returned HTML and pick out the inner content matching the
      // section.selector. If no selector, replace the whole target.
      let replacementHtml;
      try {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (section.selector) {
          const found = parsed.querySelector(section.selector);
          replacementHtml = found ? found.innerHTML : html;
        } else {
          const found = parsed.getElementById(targetId);
          replacementHtml = found ? found.innerHTML : html;
        }
      } catch (_) {
        replacementHtml = html;
      }

      const innerTarget = section.selector
        ? target.querySelector(section.selector) || target
        : target;
      innerTarget.innerHTML = replacementHtml;
    });
  }

  // Initial map load + first reconciliation. Fires once per full page load.
  function bootstrap() {
    refreshCartMap().then(reconcileBrokenGroups);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // bfcache restore — page came back from browser history, cart may have
  // changed in the interim.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) bootstrap();
  });
})();
